import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { readFirstSaleOnboarding, goToFirstSaleOnboardingStep } from "../utils/firstSaleOnboarding";
import {
  Package,
  Barcode,
  Save,
  Check,
  RefreshCcw,
  Tag,
  Ruler,
  Percent,
  Image as ImageIcon,
  Layers,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Info,
  ChevronDown,
  ShieldAlert,
  ArrowLeft,
  X,
  DollarSign,
  Settings,
  Calculator,
  Coins,
  Award, // ✅ IMPORTADO: Icono para la marca
  CalendarClock, // Vencimientos y lotes
} from "lucide-react";
import toast from "react-hot-toast";
import { isVariantTracked } from "../utils/variantStock";
import { isExpiryTrackingEnabled } from "../utils/stockLotes";
import { buildProductStatusPatch, normalizeProductStatus } from "../utils/productStatus";
import { resizeImageFile } from "../utils/imageResize";

const COST_ONLY_PRODUCT_TYPES = new Set(["Materia Prima", "Insumo", "Insumo / Materia Prima"]);
const PRODUCT_IMAGES_BUCKET = "products";

const sanitizePersistedImageUrl = (value) => {
  const url = String(value || "").trim();
  if (!url || url.startsWith("blob:")) return "";
  return url;
};

const getImageUploadErrorMessage = (error) => {
  const message = String(error?.message || "");

  if (/bucket not found/i.test(message)) {
    return `No existe el bucket de Storage "${PRODUCT_IMAGES_BUCKET}".`;
  }

  return "No se pudo subir la foto.";
};

const ProductoNuevo = ({ onNavigate, id: propId }) => {
  const { profile, organization, loading: authLoading } = useAuth();
  // Vencimientos y lotes: el lógico de empresa habilita la marca por producto.
  const expiryTrackingOn = isExpiryTrackingEnabled(organization);
  const { id: paramId } = useParams();
  
  const activeId = propId || paramId;
  const isEdit = Boolean(activeId);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Data maestra
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  
  // Org settings
  const [orgSettings, setOrgSettings] = useState({ 
    enable_variants: false,
    usd_exchange_rate: 0,
    allow_zero_sell_price: false,
  });

  const [enableVariants, setEnableVariants] = useState(false); 
  // Talles y colores: el producto en edición maneja stock por talle/color.
  const [productStockTracking, setProductStockTracking] = useState('product');

  // Estado del formulario
  const [form, setForm] = useState({
    name: "",
    description: "",
    category_id: "",
    brand: "", // ✅ NUEVO CAMPO: Marca
    product_type: "Producto Terminado",
    barcode: "",
    uom_id: "",
    
    // Costos y Precios Base
    cost_price: 0,
    target_margin: 30,
    sell_price: 0,
    iva_percentage: 21,
    price_usd: 0,
    // Toggle explícito: si está en true, el producto se cotiza en dólares y el sell_price
    // se calcula a partir de price_usd × cotización. Si está en false, los precios viven
    // en ARS y price_usd queda como dato informativo (sin auto-conversión).
    priced_in_usd: false,

    min_stock: 0,
    active: true,
    auto_update_price: false,
    
    // Flags de bloqueo
    block_discounts: false,
    block_surcharges: false,

    // false = insumo de solo costo (mano de obra, agua, energia): suma al costo
    // de la produccion pero no se valida ni descuenta de stock al producir.
    descuenta_stock_produccion: true,

    // Vencimientos y lotes (solo con el lógico de empresa prendido).
    controla_vencimiento: false,
    vida_util_dias: "",

    image_url: ""
  });

  const [variantRows, setVariantRows] = useState([]); 
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [persistedImageUrl, setPersistedImageUrl] = useState("");
  const imageInputRef = useRef(null);
  const isCostOnlyProduct = useMemo(
    () => COST_ONLY_PRODUCT_TYPES.has(String(form.product_type || "").trim()),
    [form.product_type]
  );
  const allowZeroSellPrice = Boolean(orgSettings.allow_zero_sell_price);

  // --- Helpers (Definidos antes de usarlos) ---
  const goBack = () => {
    if (onNavigate) onNavigate('productos');
    else navigate('/sistema/productos');
  };

  const normalizeMoney = (n) => {
    const v = Number(n);
    if (Number.isNaN(v)) return 0;
    return Math.max(0, v);
  };

  const normalizePercent = (n, fallback = 0) => {
    const v = Number(n);
    if (Number.isNaN(v)) return fallback;
    return Math.max(0, v);
  };

  // --- KPIs / Cálculos ---
  const kpis = useMemo(() => {
    const cost = normalizeMoney(form.cost_price);
    const sell = normalizeMoney(form.sell_price);
    const markup = Number(form.target_margin) || 0;

    // Precio sugerido = Costo + (Costo * Margen%)
    const suggestedPrice = cost > 0 ? cost * (1 + markup / 100) : 0;
    
    let realMargin = 0;
    if (sell > 0 && cost >= 0) {
      realMargin = ((sell - cost) / sell) * 100;
    } else if (cost > 0 && sell > 0) {
       realMargin = ((sell - cost) / cost) * 100;
    }

    const hasPrice = sell > 0;
    const hasName = Boolean(form.name?.trim());
    const ready = hasName && (isCostOnlyProduct || hasPrice);

    return {
      cost,
      suggestedPrice,
      sell,
      ready,
      realMargin
    };
  }, [form.cost_price, form.sell_price, form.target_margin, form.name, isCostOnlyProduct]);

  // --- Carga Inicial ---
  useEffect(() => {
    if (profile?.organization_id) {
      fetchMaestros();
      if (isEdit) loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, activeId]);

  const fetchMaestros = async () => {
    try {
      const [cats, uoms, org] = await Promise.all([
        supabase
          .from("product_categories")
          .select("*")
          .eq("organization_id", profile.organization_id)
          .eq("is_active", true)
          .order("name"),
        supabase
          .from("units_of_measure")
          .select("*")
          .eq("organization_id", profile.organization_id)
          .order("name"),
        supabase
          .from("organizations")
          .select("enable_variants, usd_exchange_rate, allow_zero_sell_price")
          .eq("id", profile.organization_id)
          .single()
      ]);

      setCategorias(cats.data || []);
      setUnidades(uoms.data || []);
      
      if (org.data) {
        setOrgSettings({
          enable_variants: org.data.enable_variants,
          usd_exchange_rate: Number(org.data.usd_exchange_rate) || 0,
          allow_zero_sell_price: org.data.allow_zero_sell_price ?? false
        });
        setEnableVariants(org.data.enable_variants);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error cargando maestros");
    }
  };

  const loadData = async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          product_variants(id, price, cost_price, barcode, attributes)
        `)
        .eq("id", activeId)
        .single();

      if (error) throw error;

      const normalizedProduct = normalizeProductStatus(data);
      const safePersistedImageUrl = sanitizePersistedImageUrl(normalizedProduct.image_url);
      let finalPrice = Number(normalizedProduct.sell_price) || (normalizedProduct.product_variants?.[0]?.price || 0);
      let finalCost = Number(normalizedProduct.cost_price) || (normalizedProduct.product_variants?.[0]?.cost_price || 0);

      setForm({
        name: normalizedProduct.name || "",
        description: normalizedProduct.description || "",
        category_id: normalizedProduct.category_id || "",
        brand: normalizedProduct.brand || "", // ✅ CARGA: Mapeo de la marca desde DB
        product_type: normalizedProduct.product_type || "Producto Terminado",
        barcode: normalizedProduct.barcode || "",
        uom_id: normalizedProduct.uom_id || "",
        
        cost_price: finalCost,
        target_margin: Number(normalizedProduct.target_margin) || 30,
        sell_price: finalPrice,
        iva_percentage: Number(normalizedProduct.iva_percentage) || 21,
        price_usd: Number(normalizedProduct.price_usd) || 0,
        // Si la columna aún no existe en DB (migración no aplicada), caemos al fallback
        // legacy: lo consideramos en USD solo si tenía price_usd > 0.
        priced_in_usd: typeof normalizedProduct.priced_in_usd === 'boolean'
          ? normalizedProduct.priced_in_usd
          : (Number(normalizedProduct.price_usd) || 0) > 0,

        min_stock: Number(normalizedProduct.min_stock) || 0,
        active: Boolean(normalizedProduct.active),
        auto_update_price: COST_ONLY_PRODUCT_TYPES.has(String(normalizedProduct.product_type || "").trim())
          ? false
          : Boolean(normalizedProduct.auto_update_price), 
        
        block_discounts: Boolean(normalizedProduct.block_discounts),
        block_surcharges: Boolean(normalizedProduct.block_surcharges),
        descuenta_stock_produccion: normalizedProduct.descuenta_stock_produccion !== false,
        controla_vencimiento: Boolean(normalizedProduct.controla_vencimiento),
        vida_util_dias: normalizedProduct.vida_util_dias ?? "",

        image_url: safePersistedImageUrl
      });
      setPersistedImageUrl(safePersistedImageUrl);

      setProductStockTracking(normalizedProduct.stock_tracking || 'product');

      if (!isVariantTracked(normalizedProduct) && normalizedProduct.product_variants && normalizedProduct.product_variants.length > 0) {
        const rows = normalizedProduct.product_variants.map((v, idx) => ({
            id: v.id,
            name: v.attributes?.variant_name || (idx === 0 ? "Base" : `Opción ${idx + 1}`),
            description: v.attributes?.variant_description || "",
            active: v.attributes?.variant_active !== false
        }));
        if (rows.length > 1 || (rows.length === 1 && rows[0].name !== 'Base')) {
             setVariantRows(rows);
        }
      }

    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el producto");
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const usdRate = orgSettings.usd_exchange_rate;
  // El modo USD ya no se infiere de price_usd > 0. Ahora es un toggle explícito
  // controlado por el usuario en el formulario.
  const isUsdMode = form.priced_in_usd === true;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    setForm(prev => {
      const next = { ...prev, [name]: val };

      if ((name === 'cost_price' || name === 'target_margin') && prev.auto_update_price) {
        const cost = Number(name === 'cost_price' ? val : prev.cost_price) || 0;
        const margin = Number(name === 'target_margin' ? val : prev.target_margin) || 0;
        const newPrice = cost * (1 + margin / 100);
        next.sell_price = parseFloat(newPrice.toFixed(2));
        // Solo recalculamos price_usd si el producto está marcado en dólares.
        if (prev.priced_in_usd && usdRate > 0) {
          next.price_usd = parseFloat((next.sell_price / usdRate).toFixed(2));
        }
      }

      if (name === 'sell_price') {
        const price = Number(val) || 0;
        const cost = Number(prev.cost_price) || 0;
        if (cost > 0) {
          const newMargin = ((price - cost) / cost) * 100;
          next.target_margin = parseFloat(newMargin.toFixed(2));
        }
        if (prev.priced_in_usd && usdRate > 0) {
          next.price_usd = parseFloat((price / usdRate).toFixed(2));
        }
      }

      if (name === 'price_usd') {
        const usd = Number(val) || 0;
        // Solo propagamos a sell_price si el toggle USD está activo.
        if (prev.priced_in_usd && usdRate > 0) {
          const newArs = usd * usdRate;
          next.sell_price = parseFloat(newArs.toFixed(2));
          const cost = Number(prev.cost_price) || 0;
          if (cost > 0) {
            const newMargin = ((newArs - cost) / cost) * 100;
            next.target_margin = parseFloat(newMargin.toFixed(2));
          }
        }
      }

      return next;
    });
  };

  const applyUsdToArs = (rawUsd) => {
    setForm(prev => {
        const rate = Number(orgSettings.usd_exchange_rate || 0);
        // Si el toggle USD está apagado o no hay cotización, solo actualizamos el campo USD
        // como dato informativo, SIN tocar el precio en pesos. Así el usuario puede tipear
        // libremente el sell_price en ARS sin que se lo sobreescriba.
        if (!prev.priced_in_usd || rate <= 0) {
            return { ...prev, price_usd: rawUsd };
        }
        const price_usd = rawUsd;
        const sell_price = price_usd === "" ? "" : Number((Number(price_usd) * rate).toFixed(2));
        let target_margin = prev.target_margin;
        const cost_price = normalizeMoney(prev.cost_price);
        if (cost_price > 0 && sell_price !== "") {
            target_margin = Number((((sell_price - cost_price) / cost_price) * 100).toFixed(2));
        }
        return { ...prev, price_usd, sell_price, target_margin };
    });
  };

  const handlePriceLogic = (type, rawValue) => {
      const value = rawValue;
      setForm(prev => {
          const next = { ...prev };
          if(type === 'cost') next.cost_price = value;
          if(type === 'markup') next.target_margin = value;
          if(type === 'sell') next.sell_price = value;

          const cost = Number(type === 'cost' ? value : prev.cost_price) || 0;
          const markup = Number(type === 'markup' ? value : prev.target_margin) || 0;
          const sell = Number(type === 'sell' ? value : prev.sell_price) || 0;

          if ((type === 'cost' || type === 'markup') && prev.auto_update_price) {
              const newSell = cost * (1 + markup / 100);
              next.sell_price = parseFloat(newSell.toFixed(2));
          }

          if (type === 'sell') {
              if (cost > 0) {
                  const newMargin = ((sell - cost) / cost) * 100;
                  next.target_margin = parseFloat(newMargin.toFixed(2));
              }
              // Solo recalculamos price_usd si el producto está marcado en dólares.
              if (prev.priced_in_usd && orgSettings.usd_exchange_rate > 0) {
                  next.price_usd = parseFloat((sell / orgSettings.usd_exchange_rate).toFixed(2));
              }
          }
          return next;
      });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setForm(prev => ({ ...prev, image_url: objectUrl }));
  };

  const clearSelectedImage = () => {
    setImageFile(null);
    setPersistedImageUrl("");
    setForm((prev) => ({ ...prev, image_url: "" }));

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return form.image_url || "";
    try {
      setUploadingImage(true);
      // Se achica ANTES de subir: la tienda servía las fotos por el endpoint de
      // transformación de Supabase, que se cobra por imagen origen distinta y
      // agotó la cuota del plan. Ver src/utils/imageResize.js
      const optimizada = await resizeImageFile(imageFile);
      const ext = (optimizada.name || "").split(".").pop() || "jpg";
      const filePath = `${profile.organization_id}/products/${Date.now()}-${Math.random().toString(16).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage.from(PRODUCT_IMAGES_BUCKET).upload(filePath, optimizada, { cacheControl: "3600", upsert: true, contentType: optimizada.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from(PRODUCT_IMAGES_BUCKET).getPublicUrl(filePath);
      return data?.publicUrl || "";
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id) return;

    const name = (form.name || "").trim();
    if (!name) {
      toast.error("Ingresá un nombre para el producto");
      return;
    }
    if (!isCostOnlyProduct && !allowZeroSellPrice && normalizeMoney(form.sell_price) <= 0) {
      toast.error("Definí un precio final mayor a 0 o activá la regla para permitir $0");
      return;
    }

    // === DEMO GUIADO = MOCK ===
    // Si estamos en el recorrido de primera venta (pasos 'welcome-demo' o
    // 'create-product', ambos en esta pantalla), el alta es una SIMULACION:
    // no escribimos nada en la base. Construimos un producto sintetico en
    // memoria, lo pasamos al paso de venta y dejamos que el POS lo "venda"
    // tambien de forma simulada. Asi nada del onboarding queda grabado.
    const demoFlow = readFirstSaleOnboarding();
    const inDemoCreateStep =
      !isEdit &&
      demoFlow?.status === 'active' &&
      ['welcome-demo', 'create-product'].includes(demoFlow?.stepId);

    if (inDemoCreateStep) {
      const demoProductId = `demo-product-${Date.now()}`;
      setIsSuccess(true);
      toast.success('Producto de demo listo (simulado, no se guarda en el sistema)');
      goToFirstSaleOnboardingStep('sell-product', {
        product: {
          id: demoProductId,
          name: name.toUpperCase(),
          costPrice: normalizeMoney(form.cost_price),
          sellPrice: normalizeMoney(form.sell_price),
        },
      });
      setTimeout(() => {
        if (onNavigate) onNavigate('ventas-pos');
        else navigate('/sistema/ventas-pos');
      }, 800);
      return;
    }

    setSaving(true);
    setIsSuccess(false);

    try {
      let imageUrl = imageFile
        ? (persistedImageUrl || "")
        : (form.image_url || persistedImageUrl || "");

      if (imageFile) {
        try {
          imageUrl = await uploadImageIfNeeded();
        } catch (err) {
          console.error(err);
          toast.error(getImageUploadErrorMessage(err));
          imageUrl = persistedImageUrl || "";
        }
      }

      const productPayload = {
        organization_id: profile.organization_id,
        name: name.toUpperCase(),
        description: form.description,
        category_id: form.category_id || null,
        brand: (form.brand || "").trim(), // ✅ ENVÍO: Se guarda la marca limpia
        product_type: form.product_type, 
        barcode: form.barcode || null,
        uom_id: form.uom_id || null,
        cost_price: normalizeMoney(form.cost_price),
        target_margin: normalizeMoney(form.sell_price) > 0 ? Number(form.target_margin || 0) : 0,
        sell_price: normalizeMoney(form.sell_price),
        iva_percentage: normalizePercent(form.iva_percentage, 21),
        price_usd: normalizeMoney(form.price_usd),
        priced_in_usd: !!form.priced_in_usd,
        min_stock: Number(form.min_stock || 0),
        ...buildProductStatusPatch(form.active),
        auto_update_price: isCostOnlyProduct ? false : form.auto_update_price,
        block_discounts: form.block_discounts,
        block_surcharges: form.block_surcharges,
        descuenta_stock_produccion: form.descuenta_stock_produccion !== false,
        image_url: imageUrl
      };
      // Vencimientos: solo se escribe con el lógico prendido, así una empresa que
      // no lo usa no toca estas columnas. Talles y colores no llevan lotes (fase 1).
      if (expiryTrackingOn && !isVariantTracked({ stock_tracking: productStockTracking }) && !isCostOnlyProduct) {
        const vida = Number(form.vida_util_dias);
        productPayload.controla_vencimiento = Boolean(form.controla_vencimiento);
        productPayload.vida_util_dias = form.controla_vencimiento && Number.isFinite(vida) && vida >= 1
          ? Math.min(3650, Math.round(vida))
          : null;
      }

      let productId = activeId;

      if (isEdit) {
        const { error } = await supabase.from("products").update(productPayload).eq("id", activeId);
        if (error) throw error;
      } else {
        const { data: newProd, error } = await supabase.from("products").insert([productPayload]).select("id").single();
        if (error) throw error;
        productId = newProd.id;
      }

      // Talles y colores: si el producto maneja stock por talle/color, sus
      // variantes se editan SOLO desde la matriz del detalle
      // (fn_guardar_variantes_producto). Este bloque legacy las borraría o les
      // copiaría el barcode del producto. Los precios de las variantes sin
      // precio propio los sincroniza la base (trg_products_sync_precio_variantes).
      const { data: trackingRow } = await supabase
        .from("products")
        .select("stock_tracking")
        .eq("id", productId)
        .maybeSingle();

      if (!isVariantTracked(trackingRow)) {
        // === Sincronizar variantes ===
        // Si el flag de variantes está activo y el usuario configuró variantRows, los
        // sincronizamos como fuente de verdad (insert/update/delete). Si no, mantenemos
        // el comportamiento original: una sola variante "Base" alineada al producto.
        const { data: existingVariants } = await supabase
          .from("product_variants")
          .select("id")
          .eq("product_id", productId);
        const existingIds = new Set((existingVariants || []).map((v) => v.id));

        const useCustomVariants =
          enableVariants && Array.isArray(variantRows) && variantRows.length > 0;

        if (useCustomVariants) {
          const incomingIds = new Set();

          for (const row of variantRows) {
            const variantPayload = {
              product_id: productId,
              organization_id: profile.organization_id,
              price: normalizeMoney(form.sell_price),
              cost_price: normalizeMoney(form.cost_price),
              barcode: form.barcode || null,
              attributes: {
                variant_name: String(row.name || "").trim(),
                variant_description: String(row.description || "").trim(),
                variant_active: row.active !== false,
              },
            };

            if (row.id && existingIds.has(row.id)) {
              incomingIds.add(row.id);
              const { error: upErr } = await supabase
                .from("product_variants")
                .update(variantPayload)
                .eq("id", row.id);
              if (upErr) throw upErr;
            } else {
              const { data: inserted, error: insErr } = await supabase
                .from("product_variants")
                .insert([variantPayload])
                .select("id")
                .single();
              if (insErr) throw insErr;
              if (inserted?.id) incomingIds.add(inserted.id);
            }
          }

          const toDelete = [...existingIds].filter((id) => !incomingIds.has(id));
          if (toDelete.length > 0) {
            const { error: delErr } = await supabase
              .from("product_variants")
              .delete()
              .in("id", toDelete);
            if (delErr) throw delErr;
          }
        } else if (existingIds.size > 0) {
          await supabase
            .from("product_variants")
            .update({
              price: normalizeMoney(form.sell_price),
              cost_price: normalizeMoney(form.cost_price),
              barcode: form.barcode || null,
            })
            .eq("product_id", productId);
        } else {
          await supabase.from("product_variants").insert([
            {
              product_id: productId,
              organization_id: profile.organization_id,
              price: normalizeMoney(form.sell_price),
              cost_price: normalizeMoney(form.cost_price),
              barcode: form.barcode || null,
              stock_quantity: 0,
              attributes: { variant_name: "Base", variant_description: "", variant_active: true },
            },
          ]);
        }
      }

      setPersistedImageUrl(imageUrl || "");
      setIsSuccess(true);
      toast.success(isEdit ? "Producto actualizado" : "Producto creado");

      // Alta real (fuera del demo): volvemos al listado de productos. El caso del
      // recorrido guiado se resuelve antes (simulado) y nunca llega hasta aca.
      setTimeout(() => { goBack(); }, 800);

    } catch (e) {
      console.error("Error al guardar producto:", e);
      if (e && e.code === "23505") {
        if (String(e.message).includes("products_org_barcode_ux")) {
          toast.error("El código de barras ingresado ya está registrado en otro producto.");
        } else if (String(e.message).includes("idx_unique_product_name_brand_org")) {
          toast.error("Ya existe un producto con el mismo nombre y marca en tu negocio.");
        } else {
          toast.error("Ya existe un producto con datos similares (código o nombre duplicado).");
        }
      } else if (e && e.code === "23503") {
        if (String(e.message).includes("products_category_id_fkey")) {
          toast.error("La categoría seleccionada no existe o no es válida.");
        } else if (String(e.message).includes("products_uom_id_fkey")) {
          toast.error("La unidad de medida seleccionada no es válida.");
        } else {
          toast.error("Error de integridad de datos (claves foráneas).");
        }
      } else {
        toast.error(e?.message || "Error al guardar");
      }
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const addVariantRow = () => {
    setVariantRows(prev => [...prev, { id: null, name: "", description: "", active: true }]);
  };
  const removeVariantRow = (idx) => {
    setVariantRows(prev => prev.filter((_, i) => i !== idx));
  };
  const toggleVariantActive = (idx) => {
    setVariantRows(prev => {
        const next = [...prev];
        next[idx].active = !next[idx].active;
        return next;
    });
  };

  if (loading || authLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-4 md:p-6 pb-20 font-sans text-slate-700 animate-nova-in">
      <form onSubmit={handleSubmit} className="w-full space-y-6">
        
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goBack}
              className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-700 transition-all shadow-sm"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-800 uppercase tracking-tight">
                {isEdit ? "Editar Producto" : "Nuevo Producto"}
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {isEdit ? "Modificando ficha técnica" : "Alta de item en catálogo"}
              </p>
            </div>
          </div>
          
          {/* Switch Activo */}
          <div 
            onClick={() => setForm(f => ({ ...f, active: !f.active }))}
            className={`cursor-pointer px-4 py-2 rounded-xl border flex items-center gap-2 transition-all ${
              form.active 
                ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                : "bg-slate-100 border-slate-200 text-slate-400"
            }`}
          >
            {form.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
            <span className="text-[10px] font-black uppercase tracking-widest">
              {form.active ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>

        {/* KPIs VISUALES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {/* Costo */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Costo Base</p>
              <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
                ${(kpis.cost || 0).toLocaleString("es-AR")}
              </p>
            </div>
            <Coins size={18} className="text-slate-400" />
          </div>

          {/* Precio Sugerido */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Precio Sugerido</p>
              <p className="text-[14px] font-bold text-emerald-600 tracking-tight mt-0.5">
                ${(kpis.suggestedPrice || 0).toLocaleString("es-AR")}
              </p>
            </div>
            <Calculator size={18} className="text-emerald-500" />
          </div>

          {/* Precio Final */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Precio Final</p>
              <p className={`text-[14px] font-bold tracking-tight mt-0.5 ${(kpis.sell || 0) > 0 ? "text-slate-900" : "text-amber-600"}`}>
                ${(kpis.sell || 0).toLocaleString("es-AR")}
              </p>
            </div>
            <Tag size={18} className="text-[#51A7FD]" />
          </div>
        </div>

        {/* CONTENIDO PRINCIPAL - 2 COLUMNAS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* COLUMNA IZQ: DATOS GENERALES */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* TARJETA 1: IDENTIDAD */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
                <Package size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Información Principal
                </span>
              </div>

              <div className="p-5 flex flex-col md:flex-row gap-6">
                {/* UPLOAD FOTO */}
                <div className="shrink-0 group relative w-32 h-32 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-[#51A7FD] transition-all overflow-hidden">
                  {form.image_url ? (
                    <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <ImageIcon className="text-slate-300 mb-2 group-hover:text-[#51A7FD] transition-colors" size={24} />
                      <span className="text-[9px] font-bold text-slate-400 uppercase">Subir Foto</span>
                    </>
                  )}
                  {form.image_url ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearSelectedImage();
                      }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-black/85 flex items-center justify-center shadow-sm z-10"
                      title="Quitar foto"
                    >
                      <X size={14} />
                    </button>
                  ) : null}
                  <input 
                    ref={imageInputRef}
                    type="file" 
                    accept="image/*" 
                    className="absolute inset-0 opacity-0 cursor-pointer" 
                    onChange={handleImageUpload}
                  />
                </div>

                {/* CAMPOS BASICOS */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                      Nombre del producto
                    </label>
                    <input
                      required
                      data-onboarding-id="product-name-field"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:border-[#51A7FD] transition-all placeholder:text-slate-300"
                      placeholder="Ej: Coca Cola 1.5L"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                      Descripción (Opcional)
                    </label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 outline-none focus:border-[#51A7FD] transition-all resize-none"
                      placeholder="Detalles adicionales..."
                    />
                  </div>
                </div>
              </div>

              {/* CATEGORIA, MARCA, TIPO Y BARCODE */}
              <div className="px-5 pb-6 grid grid-cols-1 md:grid-cols-12 gap-4">
                
                {/* Categoría (Mitad) */}
                <div className="md:col-span-6">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Categoría
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    {/* onInput ademas de onChange: el navegador dispara `input`
                        ANTES que `change`, asi el valor elegido queda en el
                        estado aunque algo reaccione al `change` en el mismo
                        tick (el recorrido guiado avanza de paso ahi) y React
                        restaure el value del campo controlado. Sin esto se
                        elegia una categoria y el combo volvia a "Seleccionar...". */}
                    <select
                      data-onboarding-id="product-category-field"
                      name="category_id"
                      value={form.category_id}
                      onChange={handleChange}
                      onInput={handleChange}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all appearance-none uppercase cursor-pointer"
                    >
                      <option value="">Seleccionar...</option>
                      {categorias.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                  </div>
                </div>

                {/* ✅ Marca (Mitad) - Nuevo */}
                <div className="md:col-span-6">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Marca
                  </label>
                  <div className="relative">
                    <Award className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      data-onboarding-id="product-brand-field"
                      name="brand"
                      value={form.brand}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all placeholder:text-slate-300"
                      placeholder="Ej: Nike, Coca-Cola..."
                    />
                  </div>
                </div>

                {/* Tipo de Producto */}
                <div className="md:col-span-6">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Tipo de Producto
                  </label>
                  <div className="relative">
                    <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <select
                      name="product_type"
                      value={form.product_type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        const isCostOnly = COST_ONLY_PRODUCT_TYPES.has(String(nextType || "").trim());
                        setForm((prev) => ({
                          ...prev,
                          product_type: nextType,
                          auto_update_price: isCostOnly ? false : prev.auto_update_price,
                          // Un servicio (ej. mano de obra) no tiene stock: lo sugerimos
                          // como "solo costo". Se puede volver a prender a mano.
                          descuenta_stock_produccion: nextType === "Servicio"
                            ? false
                            : (prev.product_type === "Servicio" ? true : prev.descuenta_stock_produccion)
                        }));
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all appearance-none uppercase cursor-pointer"
                    >
                      <option value="Producto Terminado">Producto Terminado</option>
                      <option value="Materia Prima">Materia Prima</option>
                      <option value="Insumo">Insumo</option>
                      <option value="Servicio">Servicio</option>
                      <option value="Combo">Combo / Pack</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                  </div>
                </div>

                {/* Código de Barras */}
                <div className="md:col-span-6">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Código de Barras
                  </label>
                  <div className="relative">
                    <Barcode className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      data-onboarding-id="product-barcode-field"
                      name="barcode"
                      value={form.barcode}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all placeholder:text-slate-300 uppercase"
                      placeholder="EAN / SKU"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* TARJETA 2: CONFIGURACIÓN */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
                <Settings size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  Configuración
                </span>
              </div>

              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                      Unidad de Medida
                    </label>
                    <div className="relative">
                      <Ruler className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <select
                        name="uom_id"
                        value={form.uom_id}
                        onChange={handleChange}
                        onInput={handleChange}
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all appearance-none uppercase cursor-pointer"
                      >
                        <option value="">Unidad (Default)</option>
                        {unidades.map(u => <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>)}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                      Stock Mínimo (Alerta)
                    </label>
                    <input
                      type="number"
                      onFocus={(e) => e.target.select()}
                      name="min_stock"
                      value={form.min_stock}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all placeholder:text-slate-300"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* ✅ BLOQUE DE RESTRICCIONES */}
                <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={16} className="text-slate-400" />
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-700">Bloquear Descuentos</p>
                        <p className="text-[8px] font-bold text-slate-400">No permite rebajas en POS</p>
                      </div>
                    </div>
                    <div 
                      onClick={() => setForm(f => ({ ...f, block_discounts: !f.block_discounts }))}
                      className={`cursor-pointer transition-colors ${form.block_discounts ? 'text-rose-500' : 'text-slate-300'}`}
                    >
                      {form.block_discounts ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={16} className="text-slate-400" />
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-700">Bloquear Recargos</p>
                        <p className="text-[8px] font-bold text-slate-400">No permite aumentos en POS</p>
                      </div>
                    </div>
                    <div 
                      onClick={() => setForm(f => ({ ...f, block_surcharges: !f.block_surcharges }))}
                      className={`cursor-pointer transition-colors ${form.block_surcharges ? 'text-rose-500' : 'text-slate-300'}`}
                    >
                      {form.block_surcharges ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </div>
                  </div>

                  {expiryTrackingOn && !isCostOnlyProduct && !isVariantTracked({ stock_tracking: productStockTracking }) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CalendarClock size={16} className="text-slate-400" />
                          <div>
                            <p className="text-[10px] font-black uppercase text-slate-700">Controla vencimiento</p>
                            <p className="text-[8px] font-bold text-slate-400">
                              {form.controla_vencimiento
                                ? 'Se pide la fecha de vencimiento al recibir y se vende primero lo que vence antes'
                                : 'Sin fecha de vencimiento ni lotes'}
                            </p>
                          </div>
                        </div>
                        <div
                          onClick={() => setForm(f => ({ ...f, controla_vencimiento: !f.controla_vencimiento }))}
                          className={`cursor-pointer transition-colors ${form.controla_vencimiento ? 'text-emerald-500' : 'text-slate-300'}`}
                        >
                          {form.controla_vencimiento ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                        </div>
                      </div>
                      {form.controla_vencimiento && (
                        <label className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between gap-2">
                          <span className="text-[9px] font-bold text-slate-500 uppercase">Vida útil (sugiere el vencimiento)</span>
                          <span className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="1"
                              max="3650"
                              step="1"
                              inputMode="numeric"
                              value={form.vida_util_dias ?? ""}
                              onChange={(e) => setForm(f => ({ ...f, vida_util_dias: e.target.value }))}
                              placeholder="—"
                              className="w-16 px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 text-right"
                            />
                            <span className="text-[9px] font-bold text-slate-500">días</span>
                          </span>
                        </label>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 md:col-span-2">
                    <div className="flex items-center gap-2">
                      <Layers size={16} className="text-slate-400" />
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-700">Descuenta stock al producir</p>
                        <p className="text-[8px] font-bold text-slate-400">
                          {form.descuenta_stock_produccion !== false
                            ? 'Usado como insumo de una receta, se valida y descuenta del stock'
                            : 'Solo costo: suma al costo de la receta sin tocar stock (mano de obra, agua, energía)'}
                        </p>
                      </div>
                    </div>
                    <div
                      onClick={() => setForm(f => ({ ...f, descuenta_stock_produccion: f.descuenta_stock_produccion === false }))}
                      className={`cursor-pointer transition-colors ${form.descuenta_stock_produccion !== false ? 'text-emerald-500' : 'text-slate-300'}`}
                    >
                      {form.descuenta_stock_produccion !== false ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* COLUMNA DER: PRECIOS */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-[#3FF2AA]" />
                  <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Económico
                  </span>
                </div>
                {/* USD Badge */}
                {usdRate > 0 && (
                  <div className="px-2 py-1 bg-white/10 text-white rounded-lg text-[8px] font-black border border-white/10">
                    USD: ${usdRate}
                  </div>
                )}
              </div>

              <div className="p-5 space-y-5">
                
                {/* Precio USD (Opcional) */}
                <div className={`p-4 rounded-xl border transition-all ${isUsdMode ? 'bg-blue-50/50 border-blue-200' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      Precio en dólares
                    </label>
                    {/* Toggle maestro: producto cotizado en USD sí/no */}
                    <button
                      type="button"
                      onClick={() => {
                        if (isCostOnlyProduct) return;
                        setForm(prev => ({ ...prev, priced_in_usd: !prev.priced_in_usd }));
                      }}
                      disabled={isCostOnlyProduct}
                      className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border transition-all ${
                        isUsdMode
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-400 border-slate-200'
                      } ${isCostOnlyProduct ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={isUsdMode
                        ? 'Producto cotizado en USD: el precio en ARS se recalcula con la cotización.'
                        : 'Producto en ARS: el precio se gestiona directamente en pesos.'}
                    >
                      {isUsdMode ? '✓ En dólares' : 'En pesos'}
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">US$</span>
                    <input
                      type="number"
                      onFocus={(e) => e.target.select()}
                      name="price_usd"
                      value={form.price_usd}
                      onChange={(e) => applyUsdToArs(e.target.value)}
                      disabled={isCostOnlyProduct || !isUsdMode}
                      className={`w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-black text-slate-800 outline-none focus:border-[#51A7FD] ${
                        !isUsdMode ? 'opacity-60' : ''
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {isCostOnlyProduct ? (
                    <p className="mt-2 text-[9px] text-slate-400 font-medium leading-tight">
                      Para materias primas e insumos, el precio de venta es opcional.
                    </p>
                  ) : !isUsdMode ? (
                    <p className="mt-2 text-[9px] text-slate-400 font-medium leading-tight">
                      Activá "En dólares" si querés que el precio se calcule automáticamente con la cotización del día.
                    </p>
                  ) : usdRate <= 0 ? (
                    <p className="mt-2 text-[9px] text-amber-600 font-medium leading-tight">
                      Cargá la cotización USD en Mi Empresa para que el precio en pesos se calcule automáticamente.
                    </p>
                  ) : allowZeroSellPrice ? (
                    <p className="mt-2 text-[9px] text-blue-500 font-medium leading-tight">
                      Cotización activa: ${usdRate}. El precio en pesos se calcula desde este valor.
                    </p>
                  ) : (
                    <p className="mt-2 text-[9px] text-blue-500 font-medium leading-tight">
                      El precio en pesos se calculará automáticamente según la cotización del día (${usdRate}).
                    </p>
                  )}
                </div>

                <div className="h-px bg-slate-100" />

                {/* Costo */}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Costo (ARS)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                    <input
                      type="number"
                      onFocus={(e) => e.target.select()}
                      data-onboarding-id="product-cost-field"
                      name="cost_price"
                      value={form.cost_price}
                      onChange={(e) => handlePriceLogic("cost", e.target.value)}
                      className="w-full pl-8 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Margen y Toggle Auto Update */}
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                      Margen Objetivo (%)
                    </label>
                    <div className="relative">
                      <Percent className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="number"
                        onFocus={(e) => e.target.select()}
                        name="target_margin"
                        value={form.target_margin}
                        onChange={(e) => handlePriceLogic("markup", e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all"
                        placeholder="30"
                      />
                    </div>
                  </div>
                  
                  {/* ✅ Switch Lógico: Actualizar precio Automáticamente */}
                  <div 
                    onClick={() => {
                      if (isCostOnlyProduct) return;
                      setForm(f => ({ ...f, auto_update_price: !f.auto_update_price }));
                    }}
                    className={`h-[46px] px-3 rounded-xl border flex items-center justify-center transition-all ${isCostOnlyProduct ? 'cursor-not-allowed bg-slate-100 border-slate-200 opacity-60' : 'cursor-pointer'} ${form.auto_update_price ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                    title="Actualizar precio automáticamente al cambiar costo"
                  >
                    <RefreshCcw size={18} className={form.auto_update_price ? "text-emerald-600" : "text-slate-300"} />
                  </div>
                </div>

                {/* ✅ NUEVO: PRECIO SUGERIDO (Visual) */}
                <div className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator size={14} className="text-emerald-500" />
                    <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest">
                      Precio Sugerido
                    </span>
                  </div>
                  <span className="text-sm font-black text-emerald-700">
                    {isCostOnlyProduct ? "No aplica" : `$ ${(kpis.suggestedPrice || 0).toLocaleString("es-AR")}`}
                  </span>
                </div>

                {/* Precio Venta */}
                <div className={isUsdMode || isCostOnlyProduct ? "opacity-70" : ""}>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    {isCostOnlyProduct ? "Precio Venta Final (Opcional)" : "Precio Venta Final (ARS)"}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                    <input
                      type="number"
                      onFocus={(e) => e.target.select()}
                      data-onboarding-id="product-sell-field"
                      name="sell_price"
                      value={form.sell_price}
                      onChange={(e) => handlePriceLogic("sell", e.target.value)}
                      readOnly={isUsdMode}
                      className="w-full pl-8 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-lg font-black text-slate-800 outline-none focus:border-[#51A7FD] transition-all"
                      placeholder="0.00"
                    />
                  </div>
                  {isCostOnlyProduct && (
                    <p className="mt-2 text-[9px] text-slate-400 font-medium leading-tight">
                      PodÃ©s crear y actualizar este producto sin definir precio de venta.
                    </p>
                  )}
                  {!isCostOnlyProduct && allowZeroSellPrice && (
                    <p className="mt-2 text-[9px] text-blue-500 font-medium leading-tight">
                      Con esta regla activa tambiÃ©n podÃ©s guardar el producto con precio final $0.
                    </p>
                  )}
                </div>

                {/* IVA */}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                    Alícuota IVA (%)
                  </label>
                  <select
                    data-onboarding-id="product-iva-field"
                    name="iva_percentage"
                    value={form.iva_percentage}
                    onChange={handleChange}
                    onInput={handleChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 outline-none focus:border-[#51A7FD] uppercase cursor-pointer"
                  >
                    <option value="21">21% (General)</option>
                    <option value="10.5">10.5% (Reducido)</option>
                    <option value="27">27% (Servicios)</option>
                    <option value="0">0% (Exento)</option>
                  </select>
                </div>

              </div>
            </div>

            {/* Talles y colores: las variantes se editan en la matriz del detalle */}
            {isVariantTracked({ stock_tracking: productStockTracking }) && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-start gap-3">
                <Layers size={16} className="text-[#51A7FD] mt-0.5 shrink-0" />
                <div className="text-[11px] text-slate-600 leading-relaxed">
                  <span className="font-bold text-slate-800">Este producto maneja talles y colores.</span>{' '}
                  Los talles, colores, códigos, precios propios y el stock por talle se editan desde el detalle del producto, en la sección “Talles y colores”.
                </div>
              </div>
            )}

            {/* Variantes Info (Si están activas) */}
            {enableVariants && !isVariantTracked({ stock_tracking: productStockTracking }) && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Layers size={16} className="text-[#3FF2AA]" />
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">
                      Opciones del producto
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={addVariantRow}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white/10 text-white font-black uppercase tracking-widest text-[9px] hover:bg-white/20 transition-all"
                  >
                    <Plus size={12} /> Agregar
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  {(variantRows.length ? variantRows : [{ id: null, name: "Base", description: "", active: true }]).map(
                    (v, idx) => {
                      const active = Boolean(v.active);
                      return (
                        <div key={v.id || `new-${idx}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                  Opción {idx + 1}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => toggleVariantActive(idx)}
                                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-[9px] font-bold uppercase tracking-widest transition-all ${
                                    active ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-400"
                                  }`}
                                >
                                  {active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                  {active ? "Activa" : "Inactiva"}
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                <input
                                  type="text"
                                  placeholder="Ej: ROJA XL"
                                  value={v.name}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setVariantRows((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], name: val };
                                      return next;
                                    });
                                  }}
                                  className="w-full bg-white border border-slate-200 px-3 py-2 rounded-lg text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
                                />
                                <input
                                  type="text"
                                  placeholder="Descripción opcional"
                                  value={v.description}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setVariantRows((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], description: val };
                                      return next;
                                    });
                                  }}
                                  className="w-full bg-white border border-slate-200 px-3 py-2 rounded-lg text-[11px] font-medium outline-none focus:border-[#51A7FD] transition-all shadow-inner"
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVariantRow(idx)}
                              className="p-2 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-200 transition-all"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* FOOTER ACTIONS */}
        <div className="flex items-center justify-end pt-2 pb-10">
          {/* Prueba de guardado real para el recorrido guiado: solo existe si el
              insert no fallo. Sin esto, el tour se cerraba con confeti al tocar
              "Crear producto" aunque el backend hubiera rechazado el alta (por
              ejemplo, codigo de barras duplicado -> 23505). */}
          {isSuccess && (
            <span data-onboarding-id="product-save-success" className="sr-only">
              Producto guardado
            </span>
          )}
          <button
            type="submit"
            data-onboarding-id="product-submit"
            disabled={saving || uploadingImage || isSuccess}
            className={`px-6 py-3 rounded-xl shadow-md transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center
              ${
                isSuccess
                  ? "bg-[#3FF2AA] text-[#0B2A36] shadow-emerald-500/20"
                  : "bg-emerald-600 text-white shadow-emerald-100 hover:scale-[1.02] active:scale-95"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {isSuccess ? (
              <span className="inline-flex items-center gap-2 animate-check-in">
                <Check size={16} strokeWidth={3} /> Guardado
              </span>
            ) : saving ? (
              <>
                <RefreshCcw className="animate-spin" size={16} /> Guardando
              </>
            ) : (
              <>
                <Save size={16} /> {isEdit ? "Guardar cambios" : "Crear producto"}
              </>
            )}
          </button>
        </div>

        <div className="h-6" />
      </form>

      <style>{`
        .animate-check-in { animation: checkIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes checkIn {
          from { transform: scale(0.92); opacity: 0.6; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default ProductoNuevo;
